// @ts-strict-ignore
import {AttrsSchema, Component} from 'panel';

// @ts-check
import {apiClient} from 'common/util/api-client';
import {ItemClickEvent, Section, SectionItem, SelectedTheme} from 'common/components/items-menu/types';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {IronContextRegistry} from 'common/report/context-registry';
import {ErrorResponse} from 'common/util/shared-client';
import {TagItem} from 'common/components/tag-input/tag-item';
import {TagInputChangeAction, TagInputChangeEvent} from 'common/components/tag-input/types';
import {addToast} from 'common/components/toast-area/toast-manager';
import {ToastType} from 'common/components/toast/types';
import {AuthorizeStatus, openSlackOauthBroadcastChannel} from 'common/util/slack';
import {ButtonTheme} from 'common/components/button/constants';

import {template} from './template';
import css from './index.inline.styl';
import {SelectedChannelsChangedDetail, SlackAuthProblem, SlackChannel, SlackChannelsList} from './types';

export interface SlackChannelSelectorState {
  loadingChannels: boolean;
  channelChoices: SlackChannel[];
  slackTeamId: string;
  selectedChannels: Map<string, SlackChannel>;
  slackAuthProblem: string;
  slackOAuthLoopResult?: AuthorizeStatus;
}

export type SlackChannelSelectorAttrs = {
  'selected-channels': SlackChannel[];
  'is-readonly'?: boolean;
  'button-theme'?: ButtonTheme;
  'button-full-width'?: boolean;
};

export type SlackChannelSelectorParams = {
  onSelectedChannelsChanged?: (event: SelectedChannelsChangedDetail) => void;
};

export interface SlackChannelSelectorJsxProps {
  attrs: SlackChannelSelectorAttrs;
  params: SlackChannelSelectorParams;
}

export class SlackChannelSelector extends Component<
  SlackChannelSelectorState,
  SlackChannelSelectorAttrs,
  EmptyObj,
  EmptyObj,
  IronContextRegistry,
  SlackChannelSelectorParams
> {
  private declare _slackAuthBroadcastChannel: BroadcastChannel;
  static get attrsSchema(): AttrsSchema<SlackChannelSelectorAttrs> {
    return {
      'button-theme': `string`,
      'button-full-width': `boolean`,
      'is-readonly': `boolean`,
      'selected-channels': `json`,
      ...super.attrsSchema,
    };
  }

  get config() {
    return {
      css,
      template,
      useShadowDom: true,
      defaultState: {
        loadingChannels: false,
        channelChoices: [],
        slackTeamId: null,
        selectedChannels: new Map<string, SlackChannel>(),
        slackAuthProblem: null,
        slackOAuthLoopResult: null,
      },
      params: {
        onSelectedChannelsChanged: Function,
      },
    };
  }

  selectChannelById(channelId: string) {
    const channel = this.state.channelChoices.find((channel) => channel.id === channelId);
    if (channel) {
      const selectedChannels = this.state.selectedChannels;
      selectedChannels.set(channel.id, channel);
      this.update({selectedChannels});
      this.dispatchSelectedChannelsChanged();
    }
  }

  deselectChannelById(channelId: string) {
    const selectedChannels = this.state.selectedChannels;
    selectedChannels.delete(channelId);
    this.update({selectedChannels});
    this.dispatchSelectedChannelsChanged();
  }

  dispatchSelectedChannelsChanged() {
    const detail = {selectedChannels: Array.from(this.state.selectedChannels.values()), teamId: this.state.slackTeamId};
    this.params.onSelectedChannelsChanged?.(detail);
  }

  connectedCallback() {
    super.connectedCallback();
    this.setAttribute(`tabindex`, `0`);
    this.addEventListener(`focus`, this.handleFocus);
    this._slackAuthBroadcastChannel = openSlackOauthBroadcastChannel();
    this._slackAuthBroadcastChannel.onmessage = (event: MessageEvent<{authStatus: AuthorizeStatus}>) => {
      this.handleSlackOauthBroadcast(event);
    };
    this.fetchSlackChannels();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener(`focus`, this.handleFocus);
    this._slackAuthBroadcastChannel?.close();
  }

  attributeChangedCallback(attr, oldValue, newValue) {
    super.attributeChangedCallback(attr, oldValue, newValue);
    if (attr === `selected-channels`) {
      const selectedChannels = new Map<string, SlackChannel>(
        (this.attr(`selected-channels`) || []).map((channel) => [channel.id, channel]),
      );
      this.update({selectedChannels});
    }
  }

  handleSlackOauthBroadcast(event: MessageEvent<{authStatus: AuthorizeStatus}>) {
    const {authStatus} = event.data;
    this.update({slackOAuthLoopResult: authStatus});
  }

  async fetchSlackChannels() {
    if (this.state.channelChoices.length || this.state.loadingChannels) {
      return;
    }

    try {
      this.update({loadingChannels: true, slackAuthProblem: null});
      const response = await apiClient.get<SlackChannelsList>(`/api/app/slack/channels`);
      if (response) {
        this.update({
          channelChoices: response.channels,
          slackTeamId: response.team_id,
          loadingChannels: false,
        });
      } else {
        this.update({loadingChannels: false});
      }
    } catch (error) {
      const details = error.response as ErrorResponse;
      let slackAuthProblem = null;
      if (details.type in SlackAuthProblem) {
        slackAuthProblem = details.type;
      }
      this.update({loadingChannels: false, slackAuthProblem});
    }
  }

  private informUserOfSlackAuthResult() {
    const success = this.state.slackOAuthLoopResult === AuthorizeStatus.Success;
    addToast({
      message: success
        ? `The Slack app has successfully updated`
        : `The Slack App could not be updated. Please try again soon.`,
      type: success ? ToastType.SUCCESS : ToastType.WARNING,
    });
  }

  handleFocus() {
    if (!this.state.slackAuthProblem) {
      this.fetchSlackChannels();
    }
    if (this.state.slackOAuthLoopResult) {
      this.informUserOfSlackAuthResult();
      this.update({slackOAuthLoopResult: null});
    }
  }

  getSlackChannelsForItemsMenu(): Array<Section<SlackChannel>> {
    const selectedChannelIds = [...this.state.selectedChannels].map((channel) => channel[0]);
    const items: Array<SectionItem<SlackChannel>> = this.state.channelChoices.map((channel) => ({
      value: channel,
      label: channel.name,
      isSelected: selectedChannelIds.includes(channel.id),
    }));

    return [
      {
        selectedTheme: SelectedTheme.Checkbox,
        items,
      },
    ];
  }

  handleReauthorizeClick = () => {
    // so when the user comes back to this window we retry
    this.update({
      slackAuthProblem: null,
    });
  };

  getChannelsAsTags = (): TagItem[] => {
    return Array.from(this.state.selectedChannels).map<TagItem>(([channelId, channel]) => {
      return {
        label: channel.name,
        value: channelId,
      };
    });
  };

  handleItemClick = (ev: ItemClickEvent<SlackChannel>) => {
    const item = ev.detail.item;
    const channel = item.value;
    if (item.isSelected) {
      this.deselectChannelById(channel.id);
    } else {
      this.selectChannelById(channel.id);
    }
  };

  handleTagInputChange = (ev: TagInputChangeEvent) => {
    const {detail} = ev;

    if (detail.action === TagInputChangeAction.RemoveTag) {
      this.deselectChannelById(detail.tag.value);
    }
  };
}

export const SlackChannelSelectorEl = defineAndMakeJsxFactory<SlackChannelSelectorJsxProps>(
  `mp-slack-channel-selector`,
  SlackChannelSelector,
);
export const SlackChannelSelectorReact = panel2react<SlackChannelSelectorJsxProps>(
  `mp-slack-channel-selector`,
  SlackChannelSelector,
);
